// ============================================================================
// Approval Stage Wizard - Utility Functions
// ============================================================================

import type { StageData, StageBasicDetails, StageApprover, SmartRules } from './types';

// ============================================================================
// Validation Functions
// ============================================================================

export const validateBasicDetails = (details: StageBasicDetails): boolean => {
  return details.stageName.trim().length > 0;
};

export const validateApprover = (approver: StageApprover): boolean => {
  if (approver.approverType === 'user') {
    return !!approver.specificUserId;
  }
  if (approver.approverType === 'role') {
    return !!approver.roleType;
  }
  // Rule-determined approver doesn't need validation here
  return true;
};

export const validateSmartRules = (smartRules: SmartRules): boolean => {
  const approvedValid = smartRules.onApproved.type !== undefined;
  const rejectedValid = smartRules.onRejected.type !== undefined;

  // If "Jump to stage" selected, must have target stage
  if (smartRules.onApproved.type === 'gotoStage' && !smartRules.onApproved.targetStageId) {
    return false;
  }

  return approvedValid && rejectedValid;
};

export const isStepComplete = (step: number, stageData: StageData): boolean => {
  switch (step) {
    case 1:
      return validateBasicDetails(stageData.basicDetails) && validateApprover(stageData.approver);
    case 2:
      return validateSmartRules(stageData.smartRules);
    case 3:
      // Notifications are optional
      return true;
    default:
      return false;
  }
};

export const getAllCompletedSteps = (stageData: StageData): Set<number> => {
  const completed = new Set<number>();
  for (let i = 1; i <= 3; i++) {
    if (isStepComplete(i, stageData)) {
      completed.add(i);
    }
  }
  return completed;
};

export const canProceedToNextStep = (currentStep: number, stageData: StageData): boolean => {
  // Step 3 (Notifications) is optional, always allow proceeding
  if (currentStep >= 3) {
    return true;
  }
  // Steps 1-2 are required
  return isStepComplete(currentStep, stageData);
};

// ============================================================================
// Data Initialization
// ============================================================================

export const getInitialStageData = (): StageData => {
  return {
    basicDetails: {
      stageName: '',
      description: '',
    },
    approver: {
      approverType: 'role',
      roleType: undefined,
      specificUserId: undefined,
      specificUserName: undefined,
      ruleDetermined: undefined,
      cannotApproveOwn: false,
    },
    smartRules: {
      rules: [],
      onApproved: {
        type: 'continue',
        targetStageId: undefined,
        targetStageName: undefined,
      },
      onRejected: {
        type: 'reject',
        targetStageId: undefined,
        targetStageName: undefined,
      },
      timeout: undefined,
    },
    notifications: {
      onApproved: [],
      onRejected: [],
    },
    allowApproverEdit: false, // Default to false for security
  };
};

// ============================================================================
// Data Migration
// ============================================================================

/**
 * Migrates legacy stage data that may have 'conditional' type for post-approval actions.
 * The 'conditional' type has been removed - rules are now always evaluated automatically.
 * This function converts 'conditional' to 'continue' as the default action.
 */
export const migrateStageData = (stageData: StageData): StageData => {
  const migratedData = { ...stageData };
  
  // Migrate onApproved if it has 'conditional' type
  if (migratedData.smartRules.onApproved.type === 'conditional' as any) {
    migratedData.smartRules.onApproved = {
      ...migratedData.smartRules.onApproved,
      type: 'continue',
    };
  }
  
  // Migrate onRejected if it has 'conditional' type (unlikely but handle it)
  if (migratedData.smartRules.onRejected.type === 'conditional' as any) {
    migratedData.smartRules.onRejected = {
      ...migratedData.smartRules.onRejected,
      type: 'reject',
    };
  }
  
  return migratedData;
};

// ============================================================================
// Rule Helpers
// ============================================================================

export const generateRuleId = (): string => {
  return `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const generateNotificationId = (): string => {
  return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const getNextRuleOrder = (existingRules: any[]): number => {
  if (existingRules.length === 0) return 1;
  return Math.max(...existingRules.map((r) => r.order)) + 1;
};

// ============================================================================
// Display Helpers
// ============================================================================

export const getApproverDisplayName = (approver: StageApprover): string => {
  if (approver.approverType === 'role') {
    switch (approver.roleType) {
      case 'DirectManager':
        return 'Direct Manager';
      case 'ProjectManager':
        return 'Project Manager';
      case 'DepartmentHead':
        return 'Department Head';
      default:
        return 'Unknown Role';
    }
  }
  if (approver.approverType === 'user') {
    return approver.specificUserName || 'Specific Person';
  }
  if (approver.approverType === 'rule') {
    return 'Rule-Determined';
  }
  return 'Not Set';
};

export const getActionDisplayName = (actionType: string): string => {
  switch (actionType) {
    case 'continue':
      return 'Continue to next stage';
    case 'gotoStage':
      return 'Jump to specific stage';
    case 'gotoEnd':
      return 'Fully approve (end workflow)';
    case 'reject':
      return 'Reject';
    case 'conditional':
      return 'Conditional routing (use rules)';
    default:
      return 'Unknown Action';
  }
};

// ============================================================================
// User-Friendly Rule Display Functions
// ============================================================================

/**
 * Converts technical field IDs to user-friendly labels
 */
const getFieldLabel = (fieldId: string): string => {
  const fieldMap: Record<string, string> = {
    'TST_TOTAL_HOURS': 'Total Hours',
    'TST_OVERTIME_HOURS': 'Overtime Hours',
    'TST_BILLABLE_HOURS': 'Billable Hours',
    'TST_RESOURCE': 'Resource Code',
    'PROJECT_TYPE': 'Project Type',
    'PROJECT_CODE': 'Project Code',
    'CLIENT_CODE': 'Client Code',
    'DEPARTMENT': 'Department',
    'IS_BILLABLE': 'Billable Status',
    'HAS_OVERTIME': 'Overtime Status',
    'SUBMIT_DATE': 'Submit Date',
  };
  return fieldMap[fieldId] || fieldId;
};

/**
 * Converts technical operators to natural language
 */
const getOperatorLabel = (operator: string): string => {
  const operatorMap: Record<string, string> = {
    '>': 'exceeds',
    '<': 'is less than',
    '=': 'equals',
    '>=': 'is at least',
    '<=': 'is no more than',
    '!=': 'is not',
    'contains': 'contains',
    'startsWith': 'starts with',
    'endsWith': 'ends with',
    'isTrue': 'is enabled',
    'isFalse': 'is disabled',
    'before': 'is before',
    'after': 'is after',
    'on': 'is on',
  };
  return operatorMap[operator] || operator;
};

/**
 * Formats the condition value in a user-friendly way
 */
const formatConditionValue = (value: any, fieldId: string): string => {
  // For hour-based fields, add "hours" unit
  if (fieldId.includes('HOURS')) {
    return `${value} hours`;
  }
  
  // For boolean fields, the operator already describes the state
  if (fieldId.includes('IS_') || fieldId.includes('HAS_')) {
    return '';
  }
  
  return value;
};

/**
 * Creates a user-friendly condition summary
 * Example: "TST_TOTAL_HOURS > 90" becomes "When Total Hours exceeds 90 hours"
 */
export const getRuleConditionSummary = (condition: any): string => {
  const fieldId = condition.field || 'Unknown Field';
  const operator = condition.operator || '=';
  const value = condition.value || '';
  
  const fieldLabel = getFieldLabel(fieldId);
  const operatorLabel = getOperatorLabel(operator);
  const formattedValue = formatConditionValue(value, fieldId);
  
  // Build the natural language sentence
  if (formattedValue) {
    return `When ${fieldLabel} ${operatorLabel} ${formattedValue}`;
  } else {
    // For boolean fields where value is not needed
    return `When ${fieldLabel} ${operatorLabel}`;
  }
};

/**
 * Creates a user-friendly action summary
 * Example: "gotoStage" becomes "Send to Stage 2 for review"
 */
export const getRuleActionSummary = (action: any): string => {
  switch (action.action) {
    case 'continue':
      return 'Continue to next stage';
    case 'skip':
      return 'Skip this stage and move forward';
    case 'gotoStage':
      const stageName = action.targetStageName || 'another stage';
      return `Send to ${stageName} for review`;
    case 'gotoEnd':
      return 'Approve immediately and complete workflow';
    case 'reject':
      return 'Reject and return to submitter';
    default:
      return 'Unknown action';
  }
};

// ============================================================================
// Mock Data Helpers
// ============================================================================

export const formatStageDataForBackend = (stageData: StageData) => {
  // This would transform the frontend data structure to match backend API
  // For now, just return as-is since we're not implementing backend
  return {
    ...stageData,
    // Add any transformations needed for backend
  };
};
